import { Suspense } from "react";
import { Node, NodeProps } from "@xyflow/react";
import {
  DefaultNodeData,
  isFocusModeEnabledAtomFamily,
  interfaceUrlAndNameAtomFamily_SUSPENDS,
} from "@zapier/canvas-state";
import {
  DEFAULT_INTERFACE_NODE_BACKGROUND_COLOR,
  DEFAULT_INTERFACE_NODE_COLOR,
} from "@zapier/canvas-constants";
import { useAtomValue } from "jotai";

import { AssetNodeProps } from "../AssetNodeSkeleton/AssetNodeDeterminer";
import { AssetNodeSkeleton } from "../AssetNodeSkeleton";
import { useUrlPreload } from "../../../../hooks/useAssetPreload";
import { getAssetStepTitlePlaceholderText } from "../../../../utils/getAssetStepTitlePlaceholderText";

function getInterfaceNodeProps(
  props: NodeProps<Node<DefaultNodeData>> & AssetNodeProps,
  onMouseEnter?: () => void,
) {
  const { isViewOnlyMode } = props;

  return {
    ...props,
    data: {
      ...props.data,
      color: props.data.color || DEFAULT_INTERFACE_NODE_COLOR,
    },
    placeholder: isViewOnlyMode
      ? ""
      : getAssetStepTitlePlaceholderText("interface"),
    iconName: "navInterfaces" as const,
    backgroundColor:
      props.backgroundColor || DEFAULT_INTERFACE_NODE_BACKGROUND_COLOR,
    onMouseEnter,
  };
}

function InterfaceNodeSkeleton(
  props: NodeProps<Node<DefaultNodeData>> & AssetNodeProps,
) {
  return <AssetNodeSkeleton {...getInterfaceNodeProps(props)} />;
}

function SuspendedInterfaceNodeContent(
  props: NodeProps<Node<DefaultNodeData>> & AssetNodeProps,
) {
  const { data, id } = props;
  const { projectId, pageId } = data;

  const isFocusModeEnabled = useAtomValue(
    isFocusModeEnabledAtomFamily("interface"),
  );

  // This atom is async and will suspend while loading
  const { url: interfaceUrl } = useAtomValue(
    interfaceUrlAndNameAtomFamily_SUSPENDS({
      projectId,
      pageId,
      isFocusModeEnabled,
      nodeId: id,
    }),
  );

  const handleMouseEnter = useUrlPreload("interface", interfaceUrl);

  return (
    <AssetNodeSkeleton {...getInterfaceNodeProps(props, handleMouseEnter)} />
  );
}

function SuspendedInterfaceNode(
  props: NodeProps<Node<DefaultNodeData>> & AssetNodeProps,
) {
  return (
    <Suspense fallback={<InterfaceNodeSkeleton {...props} />}>
      <SuspendedInterfaceNodeContent {...props} />
    </Suspense>
  );
}

export function InterfaceNode(
  props: NodeProps<Node<DefaultNodeData>> & AssetNodeProps,
) {
  const { data } = props;
  const { projectId, pageId } = data;

  // If we don't have the required IDs, show the skeleton immediately (no async data needed)
  if (!projectId || !pageId) {
    return <InterfaceNodeSkeleton {...props} />;
  }

  // Use internal Suspense to handle async loading and prevent broken UI
  return <SuspendedInterfaceNode {...props} />;
}
